import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSesion } from '../lib/sesion';
import { PASOS, minutosDe, pasosVisibles, type PasoRecorrido } from '../lib/recorrido';

/**
 * EL RECORRIDO GUIADO — el motor. El guion vive aparte, en `lib/recorrido.ts`.
 *
 * Pedido por la usuaria el 2026-08-26 con capturas de otro portal: una tarjeta
 * que señala una parte de la pantalla con «Paso 3 de 11» y sus botones Anterior
 * / Siguiente, y un saludo antes de empezar. Y con la puerta puesta: aparece
 * «una vez que los usuarios cambien la contraseña del portal a una
 * personalizada» (db/080).
 *
 * ── LAS SEIS DECISIONES DEL MOTOR ───────────────────────────────────────────
 *
 *  1. SEÑALA LA PANTALLA DE VERDAD, no una captura. El foco es un agujero en la
 *     cortina: un `box-shadow` gigantesco alrededor del rectángulo del elemento.
 *     Así lo que se ve dentro del foco es el botón real, con los datos reales de
 *     quien mira — y no hay ninguna imagen que envejezca.
 *
 *  2. LOS PASOS SE FILTRAN POR PERMISO **Y POR PRESENCIA VISIBLE**, y esto
 *     último no es lo mismo que «existe en el DOM»: el menú lateral está escrito
 *     con `hidden lg:block`, así que en un teléfono el elemento EXISTE y mide
 *     0×0. Comprobarlo con `querySelector` a secas haría que el recorrido
 *     señalara un rectángulo vacío en la esquina superior izquierda. Se mide el
 *     rectángulo (`medida`), y el paso equivalente para móvil —el botón ☰— entra
 *     por el mismo camino en cuanto es él el que está a la vista.
 *
 *  3. EL CONTADOR SE CALCULA DESPUÉS DE FILTRAR. «Paso 4 de 11» tiene que ser
 *     verdad para quien lo lee: un asesor con 4 módulos ve unos 11 pasos y
 *     MASTER con 14 ve unos 22. Los pasos se congelan AL ARRANCAR (una foto de
 *     lo que hay en pantalla) para que el total no cambie a mitad del recorrido.
 *
 *  4. SE RE-MIDE EN CADA FOTOGRAMA mientras está abierto (`requestAnimationFrame`),
 *     y solo se re-pinta si el rectángulo cambió de verdad. Es lo que hace que el
 *     foco siga al elemento durante el desplazamiento suave, cuando la barra de
 *     módulos se desliza de lado (su scroll es de un elemento, no de la ventana,
 *     y no dispara `scroll` en `window`) o si el teclado del teléfono cambia el
 *     alto del viewport. Un `getBoundingClientRect` de un elemento por fotograma
 *     no se nota; una posición congelada sí.
 *
 *  5. LA CORTINA NO CIERRA AL PULSAR FUERA. Cierran la ✕ y Escape. Un recorrido
 *     que se cierra con un toque perdido obliga a volver a empezar, y la mitad de
 *     los usuarios entran desde el teléfono.
 *
 *  6. SE MARCA COMO VISTO CUANDO SE ENTRÓ AL RECORRIDO, no al terminarlo. Quien
 *     lo abrió y lo cerró en el paso 3 ya decidió; volver a ofrecérselo en la
 *     próxima entrada es insistir. El botón «Recorrido» de la cabecera queda
 *     para repetirlo cuando quiera, que es lo que ella pidió. En cambio «Ver más
 *     tarde» en el saludo NO marca nada: ahí todavía no vio nada.
 *
 * ── Y UNA REGLA DE PANTALLA QUE VIENE DE db/023 ─────────────────────────────
 * Con una sesión SUPLANTADA (clave maestra) el recorrido no salta solo: el
 * `uid` del token es el del usuario abierto y su «primera vez» no se le puede
 * gastar a él desde otra cuenta. El botón sigue estando, para poder mirar el
 * recorrido tal como lo ve esa persona; el servidor no guarda la marca
 * (`auth-recorrido.mts`).
 */

/** Ancho de la tarjeta en escritorio. Bajo 640 px se estira de borde a borde. */
const ANCHO = 340;
/** Aire entre el foco y la tarjeta, y entre la tarjeta y el borde de la pantalla. */
const AIRE = 12;
/** Alto de partida antes de medir la tarjeta de verdad (evita el primer salto). */
const ALTO_SUPUESTO = 215;
/**
 * Lo que se espera antes de sacar el saludo. Cubre dos cosas distintas y por eso
 * no es un número redondo cualquiera:
 *  · que la página termine de pintar sus datos — si el saludo saliera sobre una
 *    pantalla a medio cargar, `pasosVisibles` descartaría elementos que aún no
 *    están y el recorrido saldría corto;
 *  · que la pantalla de «Cambiar mi contraseña» acabe de cerrarse sola (1.400 ms
 *    en `CambiarClave.tsx`), porque el caso normal del estreno es justo ese: el
 *    usuario pone su clave y el recorrido se desbloquea en ese momento. Con un
 *    retardo menor, el saludo aparecería ENCIMA del formulario.
 */
const RETARDO_SALUDO = 1600;

interface Medida { top: number; left: number; width: number; height: number }

/** El rectángulo del elemento, o `null` si no está o no se ve. */
function medida(selector: string): Medida | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  /* `hidden lg:block` deja el elemento en el DOM con 0×0, y un elemento fuera de
   * la pantalla por completo tampoco sirve para señalar nada. */
  if (r.width < 2 || r.height < 2) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const igual = (a: Medida | null, b: Medida | null) => {
  if (!a || !b) return a === b;
  return Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5
    && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
};

/**
 * Dónde va la tarjeta. Debajo del foco si cabe, encima si no, y pegada al borde
 * inferior cuando no cabe en ningún lado (un elemento más alto que la pantalla).
 * En pantallas estrechas la tarjeta ocupa el ancho disponible y se coloca en la
 * mitad libre, arriba o abajo — con 340 px fijos en un teléfono de 360 px no
 * queda sitio para nada.
 */
function colocar(foco: Medida | null, alto: number): React.CSSProperties {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const estrecha = vw < 640;

  if (!foco) {
    /* Paso sin objetivo (el saludo, el cierre): centrada. */
    return estrecha
      ? { left: AIRE, right: AIRE, top: Math.max(AIRE, (vh - alto) / 2) }
      : { left: (vw - ANCHO) / 2, top: Math.max(AIRE, (vh - alto) / 2), width: ANCHO };
  }

  const cabeAbajo = foco.top + foco.height + AIRE + alto <= vh - AIRE;
  const cabeArriba = foco.top - AIRE - alto >= AIRE;
  const top = cabeAbajo
    ? foco.top + foco.height + AIRE
    : cabeArriba
      ? foco.top - AIRE - alto
      : Math.max(AIRE, vh - alto - AIRE);

  if (estrecha) return { left: AIRE, right: AIRE, top };

  const centrado = foco.left + foco.width / 2 - ANCHO / 2;
  const left = Math.min(Math.max(AIRE, centrado), vw - ANCHO - AIRE);
  return { left, top, width: ANCHO };
}

export default function Recorrido({ pedido, onCerrar }: {
  /** El usuario pulsó el botón «Recorrido» de la cabecera. */
  pedido: boolean;
  onCerrar: () => void;
}) {
  const { sesion, tieneModulo, marcarRecorridoVisto, fraseDeEntrada, avisoPendientes } = useSesion();

  const [fase, setFase] = useState<'oculto' | 'saludo' | 'andando'>('oculto');
  const [pasos, setPasos] = useState<PasoRecorrido[]>([]);
  const [i, setI] = useState(0);
  const [foco, setFoco] = useState<Medida | null>(null);
  const [alto, setAlto] = useState(ALTO_SUPUESTO);

  const tarjeta = useRef<HTMLDivElement>(null);
  /* Se ofrece UNA vez por carga de la aplicación. Sin esto, «Ver más tarde»
   * volvería a sacar el saludo en la siguiente página, porque `Marco` no se
   * desmonta al navegar dentro de la SPA. */
  const ofrecido = useRef(false);

  const clavePropia = !!sesion?.usuario.clavePersonalizada;
  const visto = !!sesion?.usuario.recorridoVisto;
  const suplantando = !!sesion?.suplantacion;

  /**
   * Congela los pasos que este usuario va a ver AHORA y arranca.
   *
   * Se vuelve a calcular al pasar del saludo a los pasos, y no es redundante: en
   * el segundo que tarda el saludo en salir, la página puede no haber terminado
   * de pintar. Medido con Chrome headless a 390 px el 2026-08-26 — la cinta de
   * tasas y el botón «¿Cómo funciona?» todavía no estaban, así que el recorrido
   * salía con 21 pasos en vez de 23 y se comía dos explicaciones. Al recalcular
   * cuando el usuario pulsa «Iniciar recorrido» ya está todo en pantalla.
   *
   * Por eso el saludo NO promete un número exacto de pasos: sería el de antes
   * del recuento definitivo, y decir «son 21» para luego enseñar «Paso 1 de 23»
   * es la clase de detalle que hace dudar de todo lo demás.
   */
  const arrancar = useCallback((desde: 'saludo' | 'andando') => {
    const lista = pasosVisibles(tieneModulo, (sel) => medida(sel) !== null, PASOS);
    if (lista.length === 0) return;
    setPasos(lista);
    setI(0);
    setAlto(ALTO_SUPUESTO);
    setFase(desde);
  }, [tieneModulo]);

  const cerrar = useCallback((marcar: boolean) => {
    setFase('oculto');
    setFoco(null);
    onCerrar();
    if (marcar) marcarRecorridoVisto();
  }, [marcarRecorridoVisto, onCerrar]);

  /* El botón de la cabecera: entra directo al paso 1, sin saludo. Quien lo pulsa
   * ya sabe lo que pidió. */
  useEffect(() => {
    if (pedido && fase === 'oculto') arrancar('andando');
  }, [pedido, fase, arrancar]);

  /*
   * La primera vez: clave propia + no lo ha visto + no es una suplantación.
   * El retardo, en `RETARDO_SALUDO`.
   *
   * ⚠️ LA MARCA `ofrecido` SE PONE DENTRO DEL TEMPORIZADOR, NO ANTES, y no es un
   * detalle de estilo: `main.tsx` monta la aplicación en `<StrictMode>`, que en
   * desarrollo ejecuta cada efecto DOS veces (monta, desmonta, vuelve a montar).
   * Con la marca puesta antes, la primera pasada la ponía en `true`, la limpieza
   * cancelaba su temporizador y la segunda pasada se cortaba en el guardia: **el
   * saludo no salía nunca**. Medido con Chrome headless el 2026-08-26 — la
   * cabecera pintaba los dos botones y el recorrido no aparecía, sin un solo
   * error en consola. En producción no se habría visto (StrictMode no duplica
   * en el build), que es lo que lo hacía peligroso: habría funcionado en
   * producción y nadie habría podido reproducirlo en local.
   *
   * 2026-10-26 · Y espera a que se cierren la frase motivacional de la entrada
   * (`FraseMotivacional.tsx`) y los pendientes de DHALIA (`PendientesDhalia.tsx`):
   * el retardo empieza a contar desde el último cierre, para que no salgan dos
   * ventanas una encima de otra.
   */
  const esperando = fraseDeEntrada || avisoPendientes;
  useEffect(() => {
    if (ofrecido.current || !sesion || suplantando || esperando) return;
    if (!clavePropia || visto) return;
    const t = window.setTimeout(() => {
      ofrecido.current = true;
      arrancar('saludo');
    }, RETARDO_SALUDO);
    return () => window.clearTimeout(t);
  }, [sesion, clavePropia, visto, suplantando, esperando, arrancar]);

  const paso = fase === 'andando' ? pasos[i] : undefined;

  /* Decisión 4: se re-mide en cada fotograma y solo se re-pinta si cambió. */
  useEffect(() => {
    if (!paso) return;
    let vivo = true;
    const medir = () => {
      if (!vivo) return;
      const m = paso.objetivo ? medida(paso.objetivo) : null;
      setFoco((prev) => (igual(prev, m) ? prev : m));
      requestAnimationFrame(medir);
    };
    const id = requestAnimationFrame(medir);
    return () => { vivo = false; cancelAnimationFrame(id); };
  }, [paso]);

  /* Traer el objetivo a la vista al cambiar de paso. `nearest` en vertical evita
   * el salto cuando ya está visible; `center` en horizontal es lo que desliza la
   * barra de módulos hasta la ficha que se está explicando. */
  useEffect(() => {
    if (!paso?.objetivo) return;
    const el = document.querySelector(paso.objetivo);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [paso]);

  /* El alto real de la tarjeta, para colocarla sin taparse con el foco. */
  useEffect(() => {
    if (fase === 'oculto') return;
    const h = tarjeta.current?.offsetHeight;
    if (h && Math.abs(h - alto) > 1) setAlto(h);
  }, [fase, i, alto, paso]);

  const siguiente = useCallback(() => {
    setI((n) => {
      if (n + 1 < pasos.length) return n + 1;
      cerrar(true);
      return n;
    });
  }, [pasos.length, cerrar]);

  const anterior = useCallback(() => setI((n) => Math.max(0, n - 1)), []);

  /* Teclado. Escape cierra —y marca, porque ya estaba dentro— y las flechas
   * recorren. `preventDefault` en las flechas para que no desplacen la página
   * por debajo de la cortina. */
  useEffect(() => {
    if (fase === 'oculto') return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { cerrar(fase === 'andando'); return; }
      if (fase !== 'andando') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); siguiente(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); anterior(); }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [fase, cerrar, siguiente, anterior]);

  const minutos = useMemo(() => minutosDe(pasos.length), [pasos.length]);
  const nombreCorto = (sesion?.usuario.nombre ?? '').trim().split(/\s+/)[0] ?? '';

  if (fase === 'oculto' || !sesion) return null;

  // ── El saludo ────────────────────────────────────────────────────────────
  if (fase === 'saludo') {
    return (
      <div className="fixed inset-0 z-[60] grid place-items-center bg-black/55 p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="recorrido-saludo"
          className="w-full max-w-md overflow-hidden rounded-2xl bg-panel shadow-2xl"
        >
          {/* La franja es el único adorno, y lleva el magenta corporativo porque
            * esto no es un dato: es «dónde estás», que es justo el uso que el
            * sistema de diseño reserva para ese color. */}
          <div className="bg-gradient-to-r from-magenta-fondo to-magenta-fondo-2 px-5 py-4 text-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80">
              Tu clave ya es tuya
            </p>
            <h2 id="recorrido-saludo" className="mt-0.5 text-lg font-black leading-tight">
              {nombreCorto ? `Bienvenido, ${nombreCorto}` : 'Bienvenido al Portal Master'}
            </h2>
          </div>
          <div className="p-5">
            <p className="text-sm leading-relaxed text-texto-2">
              Este es el <strong className="text-texto">Portal Master de Grupo DH</strong>.
              {' '}En {minutos} {minutos === 1 ? 'minuto' : 'minutos'} te muestro qué tiene,
              {' '}para qué sirve cada sección y dónde encontrar tus cifras.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-texto-2">
              Puedes salir cuando quieras, y después lo repites con el botón{' '}
              <strong className="text-texto">Recorrido</strong> de arriba.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => cerrar(false)}
                className="rounded-lg border border-borde px-4 py-2 text-sm font-semibold
                           hover:bg-suave"
              >
                Ver más tarde
              </button>
              <button
                type="button"
                autoFocus
                /* `arrancar` y no `setFase`: recuenta los pasos con la pantalla
                 * ya pintada. Ver la cabecera de `arrancar`. */
                onClick={() => arrancar('andando')}
                className="rounded-lg bg-gradient-to-r from-magenta-fondo to-magenta-fondo-2 px-4 py-2
                           text-sm font-bold text-white shadow"
              >
                ▶ Iniciar recorrido ({minutos} min)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── El recorrido ─────────────────────────────────────────────────────────
  if (!paso) return null;
  const ultimo = i === pasos.length - 1;

  return (
    <>
      {/* Cortina. Cuando hay foco, la oscuridad la pinta el `box-shadow` del
        * agujero: esta capa solo se queda con los clics (decisión 5). */}
      <div
        className={'fixed inset-0 z-[60] ' + (foco ? '' : 'bg-black/55')}
        aria-hidden="true"
      />

      {foco && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-[61] rounded-xl ring-2 ring-magenta"
          style={{
            top: foco.top - 4,
            left: foco.left - 4,
            width: foco.width + 8,
            height: foco.height + 8,
            boxShadow: '0 0 0 9999px rgb(17 12 26 / 62%)',
          }}
        />
      )}

      <div
        ref={tarjeta}
        role="dialog"
        aria-modal="true"
        aria-labelledby="recorrido-titulo"
        className="fixed z-[62] rounded-2xl bg-panel p-4 shadow-2xl"
        style={colocar(foco, alto)}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-magenta">
              Paso {i + 1} de {pasos.length}
            </p>
            <h2 id="recorrido-titulo" className="mt-0.5 text-[15px] font-black leading-tight">
              {paso.titulo}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => cerrar(true)}
            aria-label="Cerrar el recorrido"
            className="ml-auto -mr-1 -mt-1 rounded-lg px-2 py-1 text-sm opacity-55
                       hover:bg-suave hover:opacity-100"
          >
            ✕
          </button>
        </div>

        {/* `aria-live` para que un lector de pantalla lea el paso nuevo sin que
          * haya que devolverle el foco a la tarjeta en cada avance. */}
        <p aria-live="polite" className="mt-2 text-[13px] leading-relaxed text-texto-2">
          {paso.texto}
        </p>

        <div className="mt-3 h-1 overflow-hidden rounded-full bg-suave">
          <div
            className="h-full rounded-full bg-gradient-to-r from-magenta to-magenta-claro
                       transition-all"
            style={{ width: `${((i + 1) / pasos.length) * 100}%` }}
          />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => cerrar(true)}
            className="rounded-lg px-2 py-1.5 text-[11px] font-semibold text-texto-2
                       hover:bg-suave"
          >
            Salir del recorrido
          </button>
          <div className="ml-auto flex items-center gap-2">
            {i > 0 && (
              <button
                type="button"
                onClick={anterior}
                className="rounded-lg border border-borde px-3 py-1.5 text-xs font-semibold
                           hover:bg-suave"
              >
                ‹ Anterior
              </button>
            )}
            <button
              type="button"
              autoFocus
              onClick={siguiente}
              className="rounded-lg bg-gradient-to-r from-magenta-fondo to-magenta-fondo-2 px-3.5 py-1.5
                         text-xs font-bold text-white shadow"
            >
              {ultimo ? 'Listo' : 'Siguiente ›'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
